import { getCompanyDomain } from '@aibees/elephant/components/company-details/utils';
import { Chip, Icon, TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { capitalize } from 'lodash-es';
import { memo, useMemo } from 'react';
import { useSuggestionSearchBarContext } from '../../context';
import { DetailHeader, type SocialLink } from '../detail-header';
import { DetailInfo } from '../detail-info';
import * as Styled from './styles';
import * as SX from './sx';
import type { CompanyDetailProps as Props } from './types';

export const CompanyDetail = memo(({ className }: Props) => {
  const { highlightedItem, isOptionsLoading } = useSuggestionSearchBarContext();

  const products = useMemo(
    () => highlightedItem?.fullData?.keywords?.slice(0, 5) || [],
    [highlightedItem?.fullData?.keywords]
  );

  const socialLinks = useMemo((): SocialLink[] => {
    if (!highlightedItem?.fullData?.link) return [];

    const { link } = highlightedItem.fullData;
    const domain = getCompanyDomain(link);

    const links: SocialLink[] = [
      {
        isDomain: true,
        href: domain,
        icon: 'link-out' as const,
      },
      {
        href: link.linkedin,
        icon: 'linkedin' as const,
      },
      {
        href: link.twitter,
        icon: 'twitter' as const,
      },
      {
        href: link.facebook,
        icon: 'facebook' as const,
      },
      {
        href: link.instagram,
        icon: 'instagram' as const,
      },
    ];

    return links.filter((socialLink) => socialLink.href); // Only show links that have URLs
  }, [highlightedItem?.fullData]);

  const companyData = highlightedItem?.fullData;
  const isLoading = isOptionsLoading;

  if (isLoading && !highlightedItem) {
    return (
      <Box sx={SX.skeletonWrapper}>
        <Skeleton animation="wave" variant="rounded" sx={SX.skeleton} />
      </Box>
    );
  }

  if (!highlightedItem) {
    return null;
  }

  return (
    <Styled.Wrapper
      className={classnames('CompanyDetail', className)}
      sx={SX.root}
    >
      <Stack>
        <Box justifyContent="space-between" alignItems="center" display="flex">
          <DetailHeader
            avatarSrc={companyData?.summary?.logo?.source || ''}
            links={socialLinks}
            name={companyData?.summary?.name || ''}
            id={highlightedItem.id}
            icon="business"
          />
        </Box>

        <Stack gap={1.5}>
          <DetailInfo
            icon="location"
            label="Company Location"
            value={companyData?.location?.headquarter?.raw_address || 'N/A'}
            isLoading={isLoading}
          />

          <DetailInfo
            icon="engineering-outline"
            label="Primary Industry"
            value={companyData?.summary?.industry || 'N/A'}
            isLoading={isLoading}
          />

          <DetailInfo
            icon="ic-type"
            label="Type"
            value={companyData?.summary?.type || 'N/A'}
            isLoading={isLoading}
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            gap={0.5}
          >
            <Stack alignItems="center" justifyContent="center">
              <Icon name="groups-outlined" color="grey.500" size={18} />
            </Stack>

            <Typography variant="body4" fontWeight={500} color="grey.700">
              Employee Size:
            </Typography>

            <TextEllipsis
              variant="body4"
              color="grey.700"
              fontWeight={700}
              value={
                companyData?.summary?.staff?.range?.start
                  ? `${companyData?.summary?.staff?.range?.start}${
                      companyData?.summary?.staff?.range?.end
                        ? ` - ${companyData?.summary?.staff?.range?.end}`
                        : ''
                    } +`
                  : 'N/A'
              }
            />
          </Stack>

          <DetailInfo
            icon="package"
            label="Product & Services"
            isLoading={isLoading}
            onlyTitle
          >
            {products.length > 0 ? (
              <Styled.ProductsWrapper direction="row" gap={0.5}>
                {products.map((product) => (
                  <Chip
                    key={product}
                    size="small"
                    label={capitalize(product)}
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Styled.ProductsWrapper>
            ) : (
              <Typography variant="body4" color="grey.500" fontWeight={400}>
                No information available.
              </Typography>
            )}
          </DetailInfo>
        </Stack>
      </Stack>
    </Styled.Wrapper>
  );
});

CompanyDetail.displayName = 'CompanyDetail';
